import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Button, ProgressIndicator, ErrorBanner, ReduxControls, Icon } from 'rhombus-react';
import { initialize, reduxForm, reset, FieldArray, Field, getFormValues } from "redux-form";

import { CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM } from "widgets/forms/list";
import { createImposiumProject, clearImposiumErrors } from "reducers/ImposiumProjects";
import { requiredNotNull } from "widgets/forms/validators/validators";
import { creativeNameMaxLength, creativeNameAllowedChars, CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";

import { loadCreatives } from 'reducers/Creatives';
import CompositionFields, { IMPOSIUM_COMPOSITION_DEFAULT_FORM_VALUES, compositionFormValuesToPostData } from "widgets/modals/imposium/CompositionFields";
import { creativeNameUnique } from 'widgets/modals/imposium/imposiumValidators';

import "./CreateImposiumProjectModal.css";


export function CompositionDetailsText({ index }) {
	const formValues = useSelector(getFormValues(CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM)) || {};
	const compositionSubForm = formValues?.creatives?.[index] || {};
	const { dimension, customDimension, duration, frameRate, customDuration } = compositionSubForm;

	const isCustomDimension = dimension === "custom";
	const isCustomDuration = duration?.value === "custom";

	let width = dimension?.width || 0;
	let height = dimension?.height || 0;
	if (isCustomDimension) {
		width = customDimension?.width || 0;
		height = customDimension?.height || 0;
	}

	let dur = duration?.value || 0;
	if (isCustomDuration) {
		dur = customDuration || 0;
	}

	let fps = frameRate?.value || 0;

	return <span>{`${width}x${height}, ${dur} seconds, ${fps} fps`}</span>;
}

export function CreativeWithCompositionForm(props){
	const { index, input } = props;
	const { name } = input;
	const [isHidden, setIsHidden] = useState(index > 0);

	const toggleHide = () => {
		setIsHidden(!isHidden);
	};


	return (
		<div key={name} className="dimensions-row">
			<div className="name-fields">
				<Field name={`${name}creativeName`}
					className=""
					component={ReduxControls.Input}
					placeholder="Creative name"
					label="Creative name"
					required
					validate={[requiredNotNull, creativeNameMaxLength, creativeNameAllowedChars, creativeNameUnique]}
					charMax={CREATIVE_NAME_MAX_LENGTH}
					showCharCount
				/>
				<Field name={`${name}compositionName`}
					className=""
					component={ReduxControls.Input}
					placeholder="Composition name"
					label="Composition name"
					required
					validate={[requiredNotNull]}
				/>
			</div>
			{index > 0 && (
				<div className="composition-details">
					<CompositionDetailsText index={index} />
					<span className="composition-details-separator">|</span>
					<span>
						<Button
							style="link"
							size="small"
							onClick={toggleHide}
							className="toggle-composition-fields-btn"
						>
							<span>{isHidden ? "Expand to edit" : "Collapse"}</span>
							<Icon name={isHidden ? "fas fa-caret-down" : "fas fa-caret-up"} />
						</Button>
					</span>
				</div>
			)}
			<div className={isHidden ? "hide" : ""}>
				<CompositionFields formName={CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM} creativeIndex={index} name={name} />
			</div>
		</div>
	);
}

export function CreativesFieldArray(props){
	const { fields, errors } = props;
	const MAX_NEW_CREATIVES = 10;

	useEffect(() => {
		//start with one creative
		if (fields?.length === 0) {
			fields.push(IMPOSIUM_COMPOSITION_DEFAULT_FORM_VALUES);
		}
	}, [fields]);

	const onAddCreative = () => {
		fields.push(IMPOSIUM_COMPOSITION_DEFAULT_FORM_VALUES);
	};

	return (
		<ul className="creatives">
			{fields.map((key, index, fields) => {
				return (
					<li key={key} className="field">
						<Field name={key} component={CreativeWithCompositionForm} formErrors={errors} index={index} />
						{fields.length > 1 && (<div><Icon className="remove-btn" name="far fa-times-circle" onClick={() => fields.remove(index)} /></div>)}
					</li>
				);
			})}
			{fields?.length < MAX_NEW_CREATIVES && (
				<li>
					<Button style="link" size="small" className="add-field" onClick={onAddCreative}>
						+ Add another creative
					</Button>
				</li>
			)}
		</ul>
	);
}


export function NewImposiumWithCreativesForm(props){
	const { handleSubmit, libraryId } = props;
	const dispatch = useDispatch();
	const {
		isSaving,
		errorObj,
	} = useSelector((state) => {
		return {
			isSaving: state.imposiumProjects.isSaving,
			errorObj: state.imposiumProjects.error || {},
		};
	});

	useEffect(() => {
		dispatch(clearImposiumErrors());
		dispatch(initialize(CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM));
	}, []);

	function onExit(){
		dispatch(reset(CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM));
		props.onClose();
	}

	const submitImposiumProject = (handleSubmit((values) => {
		let postData = {
			projectName: values.projectName,
		};

		if (values.creatives?.length) {
			postData.creatives = values.creatives.map((creative) => {
				return {
					name: creative.creativeName,
					composition: compositionFormValuesToPostData(creative.compositionName, creative),
				};
			});
		}

		dispatch(createImposiumProject(libraryId, postData)).then((resp) => {
			if (resp.success) {
				onExit();
				return dispatch(loadCreatives(libraryId));
			}
		});
	}));


	return (
		<form id={CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM} onSubmit={submitImposiumProject}>
			<ProgressIndicator key="loading" isLoading={isSaving} />
			{ !!Object.keys(errorObj)?.length && <ErrorBanner
				title="Sorry, please fix the errors below and try again."
				messages={[errorObj.description]} />
			}
			<Field
				name="projectName"
				className="project-name"
				label="Project Name"
				required
				validate={[requiredNotNull]}
				component={ReduxControls.Input}
			/>
			<div>
				<FieldArray component={CreativesFieldArray} props={props} name="creatives" />
			</div>
		</form>
	);
}

NewImposiumWithCreativesForm.displayName = "NewImposiumWithCreativesForm";


export default reduxForm({
	form: CREATE_IMPOSIUM_PROJECT_WITH_CREATIVES_FORM,
})(NewImposiumWithCreativesForm);
